import React, { useEffect, useState } from "react";
import axios from "../../api/api";
import { API_ROUTES } from "../../api/routes";
import loadingGif from "../../assets/Loading.gif";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSession } from "@/context/SessionProvider";

const meses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];
const diasSemana = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export default function Calendario() {
    const { user, loading: sessionLoading } = useSession();
    const [empresaId, setEmpresaId] = useState("");
    const [empresas, setEmpresas] = useState([]);
    const [anio, setAnio] = useState(new Date().getFullYear());
    const [eventos, setEventos] = useState([]);
    const [aniosDisponibles, setAniosDisponibles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mensajeDescargando, setMensajeDescargando] = useState(null);
    const [modoSeleccion, setModoSeleccion] = useState(false);
    const [seleccion, setSeleccion] = useState(new Set());
    const [resultadoBatch, setResultadoBatch] = useState(null);

    const isAdmin = [1, 5].includes(Number(user?.perfil_id));

    // --- Cargar empresas y fijar empresa por defecto (igual que Reportes)
    useEffect(() => {
        if (sessionLoading) return;
        if (!user) return;

        (async () => {
            try {
                const { data } = await axios.get(API_ROUTES.empresas, { withCredentials: true });
                setEmpresas(data || []);

                if (data?.length === 1) {
                    setEmpresaId(String(data[0].id));
                }

                if (![1, 5].includes(Number(user?.perfil_id)) && user?.empresa_id) {
                    setEmpresaId(String(user.empresa_id));
                }
            } catch (err) {
                console.error("Error al cargar empresas:", err);
            }
        })();
    }, [sessionLoading, user]);

    // --- Cargar calendario por empresa/año
    useEffect(() => {
        if (!empresaId) return;
        axios
            .get(API_ROUTES.calendarFechas(empresaId, anio))
            .then((res) => {
                setEventos(res.data.calendario || []);
                const yearsFromAPI = res.data.years || [];
                setAniosDisponibles(yearsFromAPI);

                // ✅ Si el año actual no está en la lista de años disponibles,
                // cambiar automáticamente al año más reciente con datos
                if (yearsFromAPI.length > 0 && !yearsFromAPI.includes(anio)) {
                    setAnio(yearsFromAPI[0]); // El primero es el más reciente (ORDER BY DESC)
                }

                setSeleccion(new Set());
                setResultadoBatch(null);
            })
            .catch((err) => console.error("Error al obtener fechas", err));
    }, [empresaId, anio]);

    // --- Helpers calendario
    const obtenerDiasDelMes = (mes, anioX) => {
        const dias = [];
        const primerDia = new Date(anioX, mes, 1).getDay();
        const totalDias = new Date(anioX, mes + 1, 0).getDate();
        let fila = [];
        for (let i = 0; i < primerDia; i++) fila.push("");
        for (let dia = 1; dia <= totalDias; dia++) {
            fila.push(dia);
            if (fila.length === 7) {
                dias.push(fila);
                fila = [];
            }
        }
        if (fila.length > 0) {
            while (fila.length < 7) fila.push("");
            dias.push(fila);
        }
        return dias;
    };

    const obtenerEvento = (fecha) =>
        eventos.find((e) => (e.fecha_inicio || "").slice(0, 10) === fecha);

    const descargarBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    // --- Selección simple (toggle)
    const toggleSeleccion = (fecha) => {
        setSeleccion((prev) => {
            const next = new Set(prev);
            next.has(fecha) ? next.delete(fecha) : next.add(fecha);
            return next;
        });
    };

    const handleDayClick = (fecha, evento) => {
        if (!evento || loading) return;
        if (modoSeleccion) {
            toggleSeleccion(fecha);
        } else {
            descargarDia(fecha);
        }
    };

    // --- Descarga de un día
    const descargarDia = async (fecha) => {
        const evento = obtenerEvento(fecha);
        if (!empresaId || !evento?.archivo || loading) return;

        setLoading(true);
        setMensajeDescargando(`Generando ZIP para ${fecha}...`);
        try {
            const res = await fetch(API_ROUTES.calendarDescargar(empresaId, fecha), {
                credentials: "include",
            });
            if (!res.ok) throw new Error(await res.text().catch(() => "Error al descargar"));
            const data = await res.blob();
            const blob = new Blob([data], { type: "application/zip" });
            descargarBlob(blob, `CDR_${empresaId}_${fecha}.zip`);
            toast.success(`ZIP del ${fecha} listo`);
        } catch (err) {
            console.error(err);
            toast.error(err?.message || "Error al descargar el ZIP");
        } finally {
            setLoading(false);
            setMensajeDescargando(null);
        }
    };

    // --- Descarga de selección múltiple
    const descargarSeleccion = async () => {
        if (!empresaId || seleccion.size === 0) return;

        const fechas = Array.from(seleccion).sort(
            (a, b) => new Date(a).getTime() - new Date(b).getTime()
        );

        setLoading(true);
        setMensajeDescargando(`Descargando ${fechas.length} día(s)...`);
        setResultadoBatch(null);

        let JSZip;
        try {
            JSZip = (await import("jszip")).default;
        } catch {
            JSZip = null;
        }

        const baja = async (f) => {
            try {
                const res = await fetch(API_ROUTES.calendarDescargar(empresaId, f), {
                    credentials: "include",
                });
                if (!res.ok) {
                    const msg = await res.text().catch(() => "");
                    throw new Error(msg || `HTTP ${res.status}`);
                }
                const blob = await res.blob();
                return { ok: true, fecha: f, blob };
            } catch (e) {
                return { ok: false, fecha: f, error: e?.message || "Error desconocido" };
            }
        };

        try {
            const results = await Promise.all(fechas.map(baja));
            const ok = results.filter((r) => r.ok);
            const fail = results.filter((r) => !r.ok);

            if (ok.length > 0) {
                if (JSZip) {
                    const zip = new JSZip();
                    for (const r of ok) {
                        zip.file(`CDR_${r.fecha}.zip`, r.blob);
                    }
                    const blob = await zip.generateAsync({ type: "blob" });
                    const nombre =
                        ok.length === 1
                            ? `CDR_${empresaId}_${ok[0].fecha}.zip`
                            : `CDR_${empresaId}_${ok[0].fecha}__${ok[ok.length - 1].fecha}.zip`;
                    descargarBlob(blob, nombre);
                } else {
                    for (const r of ok) {
                        descargarBlob(r.blob, `CDR_${empresaId}_${r.fecha}.zip`);
                    }
                }
            }

            if (ok.length > 0) toast.success(`Descargados ${ok.length} día(s).`);
            if (fail.length > 0) {
                const listado = fail.map((f) => f.fecha).join(", ");
                toast.warn(`No disponibles ${fail.length} día(s): ${listado}`);
            }
            if (ok.length === 0 && fail.length > 0) {
                toast.error("No se pudo descargar ninguno de los días seleccionados.");
            }

            setResultadoBatch({
                ok: ok.map((r) => r.fecha),
                fail: fail.map((r) => ({ fecha: r.fecha, error: r.error })),
            });
        } catch (err) {
            console.error(err);
            toast.error("Fallo al procesar la selección.");
        } finally {
            setLoading(false);
            setMensajeDescargando(null);
        }
    };

    return (
        <div className="p-6">
            <ToastContainer position="top-right" autoClose={3500} />
            <h1 className="text-2xl font-semibold mb-3">Calendario CDR</h1>

            {/* Barra superior */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <select
                    value={empresaId}
                    onChange={(e) => setEmpresaId(e.target.value)}
                    className={`border px-3 py-2 rounded ${!isAdmin ? "bg-gray-50 text-gray-600" : ""}`}
                    disabled={!isAdmin}
                >
                    <option value="">- Empresa -</option>
                    {empresas.map((e) => (
                        <option key={e.id} value={e.id}>
                            {e.nombre}
                        </option>
                    ))}
                </select>

                <select
                    value={anio}
                    onChange={(e) => setAnio(parseInt(e.target.value))}
                    className="border px-3 py-2 rounded"
                >
                    {aniosDisponibles.map((a) => (
                        <option key={a} value={a}>
                            {a}
                        </option>
                    ))}
                </select>

                {/* Acciones */}
                <div className="ml-auto flex items-center gap-2">
                    <button
                        onClick={() => setModoSeleccion((m) => !m)}
                        className={`px-3 py-2 rounded text-sm font-semibold transition
              ${modoSeleccion
                                ? "bg-amber-500 text-white hover:bg-amber-600"
                                : "border hover:bg-gray-50"}`}
                    >
                        {modoSeleccion ? "Terminar selección" : "Seleccionar días"}
                    </button>

                    <button
                        onClick={descargarSeleccion}
                        disabled={seleccion.size === 0 || loading}
                        className={`px-3 py-2 rounded text-sm font-semibold text-white transition
              ${seleccion.size === 0 || loading
                                ? "bg-gray-300 cursor-not-allowed"
                                : "bg-cyan-700 hover:bg-cyan-800"}`}
                    >
                        Descargar selección ({seleccion.size})
                    </button>

                    <button
                        onClick={() => {
                            setSeleccion(new Set());
                            setResultadoBatch(null);
                        }}
                        disabled={seleccion.size === 0 || loading}
                        className="px-3 py-2 rounded text-sm border hover:bg-gray-50 disabled:opacity-50"
                    >
                        Limpiar
                    </button>
                </div>

                {resultadoBatch &&
                    (resultadoBatch.fail.length > 0 || resultadoBatch.ok.length > 0) && (
                        <div className="w-full mt-2 text-sm">
                            {resultadoBatch.ok.length > 0 && (
                                <div className="text-emerald-700">
                                    ✓ Descargados: {resultadoBatch.ok.length} día(s)
                                    <span className="ml-2 text-emerald-600">
                                        [{resultadoBatch.ok.join(", ")}]
                                    </span>
                                </div>
                            )}
                            {resultadoBatch.fail.length > 0 && (
                                <div className="text-amber-700 mt-1">
                                    ⚠ No disponibles: {resultadoBatch.fail.length} día(s) — se omitieron
                                    <div className="mt-0.5 max-h-20 overflow-auto border border-amber-200 rounded p-1 bg-amber-50">
                                        {resultadoBatch.fail.map((f, i) => (
                                            <div key={i} className="leading-5">
                                                {f.fecha}{" "}
                                                <span className="text-xs text-amber-600">({f.error})</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
            </div>

            {loading && mensajeDescargando && (
                <div className="flex items-center gap-2 mb-3 text-green-700">
                    <img src={loadingGif} alt="Cargando..." className="w-5 h-5" />
                    <span className="text-sm">{mensajeDescargando}</span>
                </div>
            )}

            <p className="text-xs text-gray-500 mb-4">
                Activa <b>Seleccionar días</b> y haz clic para marcar/desmarcar.{" "}
                Cuando <b>no</b> estás en selección, un clic descarga ese día.
            </p>

            {/* Calendario */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {meses.map((mes, idx) => {
                    const semanas = obtenerDiasDelMes(idx, anio);
                    const mesStr = (idx + 1).toString().padStart(2, "0");
                    const hayDatosMes = eventos.some((e) =>
                        (e.fecha_inicio || "").startsWith(`${anio}-${mesStr}`)
                    );

                    return (
                        <div key={mes} className="bg-white rounded-lg shadow p-4 border">
                            {/* Encabezado mes */}
                            <div
                                className={`flex justify-center items-center gap-2 font-semibold text-gray-700 mb-2 relative ${hayDatosMes
                                    ? "cursor-pointer group"
                                    : "opacity-40 cursor-not-allowed"
                                    }`}
                                onClick={async () => {
                                    if (!empresaId || !hayDatosMes || loading) return;
                                    setLoading(true);
                                    setMensajeDescargando(`Generando ZIP para ${mes} ${anio}...`);
                                    try {
                                        const res = await fetch(
                                            API_ROUTES.calendarDescargarMes(empresaId, anio, mesStr),
                                            { credentials: "include" }
                                        );
                                        if (!res.ok)
                                            throw new Error(
                                                await res.text().catch(() => "Error al generar ZIP")
                                            );
                                        const blob = await res.blob();
                                        descargarBlob(blob, `CDR_${anio}_${mesStr}.zip`);
                                        toast.success(`ZIP de ${mes} ${anio} listo`);
                                    } catch (err) {
                                        console.error("Error al descargar ZIP del mes", err);
                                        toast.error(
                                            err?.message || "Error inesperado al descargar el ZIP"
                                        );
                                    } finally {
                                        setLoading(false);
                                        setMensajeDescargando(null);
                                    }
                                }}
                            >
                                <span>{mes}</span>
                                <span className="text-sm text-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                    Descargar mes completo
                                </span>
                            </div>

                            <div className="grid grid-cols-7 text-xs text-center font-medium text-gray-500">
                                {diasSemana.map((dia) => (
                                    <div key={dia}>{dia}</div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 text-sm text-center mt-1 gap-y-1">
                                {semanas.map((semana, i) =>
                                    semana.map((dia, j) => {
                                        const fecha = dia
                                            ? `${anio}-${mesStr}-${dia.toString().padStart(2, "0")}`
                                            : null;
                                        const evento = fecha ? obtenerEvento(fecha) : null;
                                        const isSel = fecha ? seleccion.has(fecha) : false;

                                        return (
                                            <div key={`${i}-${j}`} className="py-1">
                                                {dia ? (
                                                    <button
                                                        onClick={() => handleDayClick(fecha, evento)}
                                                        disabled={!evento || loading}
                                                        className={`w-8 h-8 rounded-full transition duration-150
                              ${!evento
                                                                ? "hover:bg-gray-100 cursor-not-allowed text-gray-300"
                                                                : isSel
                                                                    ? "bg-emerald-600 text-white ring-2 ring-emerald-300 shadow shadow-emerald-200"
                                                                    : "bg-cyan-600 text-white hover:bg-cyan-800"}`}
                                                        title={
                                                            !evento
                                                                ? "Sin datos"
                                                                : modoSeleccion
                                                                    ? "Clic para seleccionar"
                                                                    : "Clic para descargar"
                                                        }
                                                    >
                                                        {dia}
                                                    </button>
                                                ) : (
                                                    <div className="w-8 h-8" />
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}